<template>
  <v-card>
    <v-toolbar v-if="name" elevation="1">
      <v-toolbar-title>{{ name }}</v-toolbar-title>
    </v-toolbar>
    <v-expansion-panels v-model="panel">
      <v-expansion-panel v-for="{ _id, termText, assignment, hours, history, isEvents } of assignments" :key="termText">
        <v-expansion-panel-header>
          <v-row>
            <v-col>{{ termText }}</v-col>
            <v-col>Assigned Area: {{ assignment }}</v-col>
            <v-col>Hours Served: {{ hours || 0 }}</v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table v-if="history.length > 0 && JSON.stringify(history[0]) !== '{}'" :items="history" :headers="isEvents ? eventHeaders : headers">
            <template v-slot:item.desc="{ item }">{{ item.eventName || item.desc }}
              <v-dialog v-if="item.eventDesc" width="600">
                <template #activator="{ on }">
                  <v-btn v-on="on" x-small class="ml-3">More</v-btn>
                </template>
                <v-card>
                  <v-card-title>{{ item.eventName }}</v-card-title>
                  <v-card-text v-html="item.eventDesc" />
                  <v-card-text>Click anywhere outside this card to close it.</v-card-text>
                </v-card>
              </v-dialog>
            </template>
            <template v-slot:item.date="{ item }">{{ stringFormatDate(item.date, true, true) }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip label outlined class="mr-2">{{ item.status || 'Completed' }}</v-chip>
            </template>
            <template v-slot:item.blocks="{ item }">
              <v-chip v-for="{ _id, desc } of item.blocks" :key="_id" label outlined class="mr-2">{{ desc }}</v-chip>
            </template>
            <template v-slot:item.remove="{ item }">
              <v-btn v-if="isEmployee && (item.addedBy === user.username || isAdmin) && !item.event && typeof(_id) === 'string'" icon small @click="removeEntry(_id, item)">
                <v-icon color="error">fal fa-minus-circle</v-icon>
              </v-btn>
              <span v-else-if="isEmployee && item.addedBy === user.username && item.event">Manage in Event</span>
              <span v-else-if="!['Chapel','Dining','Facilities'].includes(assignment) && !isEmployee && item.addedBy === user.username && item.status === 'Committed'">
                <v-btn @click="openWithdrawDialog(item)">Withdraw from Event</v-btn>
              </span>
              <span v-else>Cannot remove</span>
            </template>
          </v-data-table>
          <v-alert v-else-if="isDialog" type="info" outlined>There are currently no hours entered for this student for this assignment.</v-alert>
          <v-alert v-else type="info" outlined>There are currently no hours entered for you for this assignment. If you have served in some capacity, check with your supervisor about when to expect those hours to be added to the system.</v-alert>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <slot></slot>
    <v-dialog v-model="withdrawDialog" width="400">
      <v-card>
        <v-card-title>Withdraw from Event</v-card-title>
        <v-card-text>
          <p>Please explain why you're withdrawing from this event. This information will be sent to the supervisor of this event and will be logged in your history.</p>
          <v-textarea v-model="withdrawComment" outlined />
        </v-card-text>
        <v-card-actions>
          <v-btn @click="submitWithdrawal()">Submit</v-btn>
          <v-spacer />
          <v-btn @click="withdrawDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { computed, ref, watch } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'

export default {
  props: {
    bannerId: {
      type: String,
      default: ''
    },
    isDialog: {
      type: Boolean,
      default: false
    },
    serveData: {
      type: Array,
      default: () => { return [] }
    }
  },
  setup (props, { root, emit }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const isAdmin = computed(() => user.value.roles.includes('Student Development') || user.value.roles.includes('Technology Services'))
    const isEmployee = computed(() => user.value.roles.includes('Employee'))
    const panel = ref(0)
    const name = ref('')
    const assignments = ref([])
    const term = ref('')
    const termText = computed(() => {
      const rec = assignments.value.find(({ term: t }) => t === term.value)
      if (rec) {
        return rec.termText
      }
      return ''
    })
    const termAssignment = computed(() => {
      const rec = assignments.value.find(({ term: t }) => t === term.value)
      if (rec) return rec
      return {
        assignment: '',
        hours: 0,
        history: []
      }
    })

    watch(() => props.bannerId, () => {
      if (props.bannerId) {
        loadData()
      }
    })
    watch(() => props.serveData, () => {
      if (props.serveData.length > 0) {
        // name.value = props.serveData[0].first + ' ' + props.serveData[0].last
        assignments.value = props.serveData.map((row) => {
          row.termText = row.term.map((t) => getTermText(t)).join('/')
          return row
        })
        if (term.value === '') {
          term.value = assignments.value[0].term[0]
        }
      }
    })

    function getTermText (term) {
      return (term.substring(4, 6) === '05' ? 'Spring ' : 'Fall ') + term.substring(0, 4)
    }
    async function loadData () {
      const aggregate = [
        { $match: { bannerId: props.bannerId } },
        { $unwind: {
          path: '$history',
          preserveNullAndEmptyArrays: true
        } },
        { $lookup: {
          from: 'Serve-Events',
          localField: 'history.event',
          foreignField: '_id',
          as: 'event'
        } },
        { $addFields: {
          event: {
            $first: '$event'
          }
        } },
        { $addFields: {
          event: {
            $first: {
              $filter: {
                input: '$event.students',
                as: 'stu',
                cond: {
                  $eq: ['$$stu.pidm', '$pidm']
                }
              }
            }
          }
        } },
        { $addFields: { 'history.blocks': '$event.blocks' } },
        { $group: {
          _id: {
            term: '$term',
            assignment: '$assignment',
            first: '$first',
            last: '$last'
          },
          history: { $push: '$history' }
        } },
        { $addFields: {
          term: '$_id.term',
          assignment: '$_id.assignment',
          first: '$_id.first',
          last: '$_id.last'
        } },
        { $sort: { term: 1 } }
      ]
      const { data } = await root.$feathers.service('serve/student').find({ query: { aggregate } })
      name.value = data[0].first + ' ' + data[0].last
      assignments.value = data.map((row) => {
        row.termText = row.term.map((t) => getTermText(t)).join('/')
        row.isEvents = row.assignment && !['Dining', 'Facilities', 'Grounds', 'Chapel', 'Off-Campus'].includes(row.assignment)
        return row
      })
      console.log(assignments.value)
      if (term.value === '') {
        term.value = assignments.value[0].term[0]
      }
    }

    const headers = computed(() => {
      const arr = [
        { text: 'Entered Date', value: 'date' },
        { text: 'Description', value: 'desc' },
        { text: 'Entered By', value: 'addedBy' },
        { text: 'Hours', value: 'hours' },
        { text: 'Status', value: 'status' }
      ]
      if (isEmployee.value) arr.push({ text: 'Remove', value: 'remove' })
      return arr
    })
    const eventHeaders = ref([
      { text: 'Entered Date', value: 'date' },
      { text: 'Description', value: 'desc' },
      { text: 'Entered By', value: 'addedBy' },
      { text: 'Hours', value: 'hours' },
      { text: 'Status', value: 'status' },
      { text: 'Duties/Options Selected', value: 'blocks' },
      { text: 'Remove', value: 'remove' }
    ])

    async function removeEntry (serveId, { _id: itemId, hours }) {
      await root.$feathers.service('serve/student').patch(serveId, { $pull: { history: { _id: itemId } }, $inc: { hours: (-1 * hours) } })
      loadData()
      emit('updated')
    }

    const withdrawDialog = ref(false)
    const withdrawComment = ref('')
    const withdrawId = ref('')
    async function openWithdrawDialog (item) {
      withdrawComment.value = ''
      withdrawId.value = item.event
      withdrawDialog.value = true
    }

    async function submitWithdrawal () {
      const index = assignments.value.findIndex(({ history }) => history.find(({ event }) => event === withdrawId.value))
      if (index >= 0) {
        // Mark the student as Withdrawn in the event
        await root.$feathers.service('serve/event').patch(withdrawId.value, { 'students.$[el].status': 'Withdrawn' }, { query: { arrayFilters: [{ 'el.pidm': user.value.pidm }] } })
        // Mark the student as Withdrawn in their history
        console.log(index)
        const serveId = assignments.value[index]._id
        const data = await root.$feathers.service('serve/student').patch(serveId, { 'history.$[el].status': 'Withdrawn', 'history.$[el].desc': 'Withdraw Reason: ' + withdrawComment.value }, { query: { arrayFilters: [{ 'el.event': withdrawId.value }] } })
        // Update their history list with the refreshed history
        data.termText = data.term.map((t) => getTermText(t)).join('/')
        assignments.value.splice(index, 1, data)
        withdrawDialog.value = false
      }
    }

    return {
      user,
      isAdmin,
      isEmployee,
      panel,
      name,
      assignments,
      loadData,
      term,
      termText,
      termAssignment,
      headers,
      eventHeaders,
      removeEntry,
      withdrawDialog,
      withdrawComment,
      withdrawId,
      openWithdrawDialog,
      submitWithdrawal,
      stringFormatDate
    }
  }
}
</script>
